/**
 * Bouton flottant messagerie + drawers (contacts en ligne + chat direct) +
 * heartbeat de présence. À intégrer une fois dans le layout d'un portail.
 *
 * Se compose de :
 *  - useHeartbeatPresence : ping 30s automatique
 *  - Bouton rond flottant bas-droite avec compteur de connectés
 *  - OnlineUsersDrawer : liste
 *  - PortailChatDrawer : chat direct sur une conversation
 */
import React, { useState, useEffect } from 'react';
import { MessageCircle, Users } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store';
import { openChat } from '../../store/slices/openChatsSlice';
import { useHeartbeatPresence } from '../../hooks/useHeartbeatPresence';
import { useListerPresenceQuery, PresenceItem } from '../../services/messagerieChat';
import OnlineUsersDrawer from './OnlineUsersDrawer';

interface Props {
  /** Position d'écran par défaut : 'right' (défaut) ou 'left' */
  side?: 'left' | 'right';
  /** Force le thème sombre (utile depuis portail étudiant qui n'a pas Redux ui) */
  forceDark?: boolean;
}

export const MessagerieFloating: React.FC<Props> = ({ side = 'right', forceDark }) => {
  const reduxToken = useSelector((s: RootState) => s.auth.token);
  const reduxDark = useSelector((s: RootState) => s.ui.theme) === 'dark';
  // Tokens possibles : JWT admin OU token portail étudiant
  const [portailToken, setPortailToken] = useState<string | null>(
    typeof window !== 'undefined' ? localStorage.getItem('portail_access_token') : null,
  );
  // Rester réactif aux changements de token portail (login/logout côté portail)
  useEffect(() => {
    const onStorage = () => setPortailToken(localStorage.getItem('portail_access_token'));
    window.addEventListener('storage', onStorage);
    // Poll faible : storage event ne se déclenche pas dans le même onglet
    const iv = setInterval(onStorage, 5000);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(iv); };
  }, []);
  const token = reduxToken || portailToken;
  const isDark = forceDark ?? reduxDark;
  // Heartbeat conditionnel : seulement si connecté
  useHeartbeatPresence(!!token);

  const dispatch = useDispatch();
  const [openList, setOpenList] = useState(false);

  // Compteur de connectés (poll léger toutes les 60s même quand drawer fermé)
  const { data: presence } = useListerPresenceQuery(undefined, {
    skip: !token,
    pollingInterval: 60000,
  });
  const nbConnected = presence?.total ?? 0;

  const handleOpenChat = (convId: number, user: PresenceItem) => {
    dispatch(openChat({ convId, label: user.user_nom }));
    setOpenList(false);
  };

  if (!token) return null;

  return (
    <>
      {/* Bouton flottant — z-40 pour rester au-dessus des headers sticky (z-30) */}
      <button
        onClick={() => setOpenList(true)}
        className={`fixed bottom-5 ${side === 'right' ? 'right-5' : 'left-5'} z-40 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105 ring-4 ring-blue-500/20 ${
          isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
        title="Contacts en ligne"
      >
        <MessageCircle size={26} />
        {nbConnected > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-green-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white dark:border-gray-900 shadow">
            {nbConnected > 99 ? '99+' : nbConnected}
          </span>
        )}
        {/* Petit pulse pour attirer l'œil quand il y a des connectés */}
        {nbConnected > 0 && (
          <span className="absolute inset-0 rounded-full animate-ping bg-blue-400/40 pointer-events-none" />
        )}
      </button>

      <OnlineUsersDrawer
        open={openList}
        onClose={() => setOpenList(false)}
        isDark={isDark}
        onOpenConversation={handleOpenChat}
      />
      {/* Note : PortailChatDrawer(s) sont rendus par ChatDrawersStack dans
          MainLayout — plus besoin de local state ici. Chaque appel à
          dispatch(openChat(...)) ouvre ou focus un drawer dans le stack. */}
    </>
  );
};

export default MessagerieFloating;
